import React, { useState, useEffect } from 'react';

const inquiryTypes = [
    { id: 'podcast', label: 'Podcast Guest', description: 'Invite me on your podcast' },
    { id: 'collaboration', label: 'Brand Collaboration', description: 'Partner on a project or campaign' },
    { id: 'speaking', label: 'Speaking Engagement', description: 'Conference, panel, or event' },
    { id: 'press', label: 'Press & Media', description: 'Interview or feature request' },
    { id: 'other', label: 'Other', description: 'Something else' },
];

const ContactModal = ({ isOpen, onClose }) => {
    const [selectedType, setSelectedType] = useState(null);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const email = 'hello@amandafilomena.com';

    const getEmailContent = (type) => {
        const subject = `${type.label} Inquiry — Amanda Filomena`;
        const body = `Hi Amanda,\n\nI'm reaching out regarding: ${type.label}\n\n[Please share details about your inquiry here]\n\nBest,\n[Your name]`;
        return { subject, body };
    };

    const openGmail = (type) => {
        const { subject, body } = getEmailContent(type);
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(gmailUrl, '_blank');
        handleClose();
    };

    const openDefaultMail = (type) => {
        const { subject, body } = getEmailContent(type);
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        handleClose();
    };

    const handleClose = () => {
        setSelectedType(null);
        onClose();
    };

    const handleBack = () => {
        setSelectedType(null);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-ink/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="fixed bg-cream border border-sand w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-sand">
                    {selectedType ? (
                        <button onClick={handleBack} className="text-muted hover:text-ink transition-colors flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                    ) : (
                        <h2 className="text-xl text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Make an Inquiry</h2>
                    )}
                    <button
                        onClick={handleClose}
                        className="text-muted hover:text-ink transition-colors p-1"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {!selectedType ? (
                        <>
                            <p className="text-sm text-muted mb-6">
                                Select the type of inquiry that best fits your request.
                            </p>

                            <div className="flex flex-col gap-3">
                                {inquiryTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        onClick={() => setSelectedType(type)}
                                        className="group text-left p-4 border border-sand bg-surface hover:border-terra/40 transition-all"
                                    >
                                        <p className="text-sm font-medium text-ink group-hover:text-terra transition-colors">
                                            {type.label}
                                        </p>
                                        <p className="text-xs text-muted mt-1">
                                            {type.description}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-muted mb-2">
                                {selectedType.label}
                            </p>
                            <p className="text-sm text-ink mb-6">
                                Choose how you'd like to send your inquiry:
                            </p>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => openGmail(selectedType)}
                                    className="flex items-center gap-4 p-4 border border-sand bg-surface hover:border-terra/40 transition-all"
                                >
                                    <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24">
                                        <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                                        <path fill="#34A853" d="M0 19.366V5.457c0-.31.044-.61.126-.896L12 13.273V24H1.636A1.636 1.636 0 0 1 0 22.364v-3z"/>
                                        <path fill="#4285F4" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636H12V13.273l11.874-8.712c.082.286.126.586.126.896z"/>
                                        <path fill="#FBBC05" d="M12 13.273L.126 4.561A1.636 1.636 0 0 1 1.636 3h20.728c.62 0 1.164.344 1.446.853L12 13.273z"/>
                                    </svg>
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-ink">Open in Gmail</p>
                                        <p className="text-xs text-muted">Opens Gmail in a new tab</p>
                                    </div>
                                </button>

                                <button
                                    onClick={() => openDefaultMail(selectedType)}
                                    className="flex items-center gap-4 p-4 border border-sand bg-surface hover:border-terra/40 transition-all"
                                >
                                    <svg className="w-6 h-6 flex-shrink-0 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-ink">Open in Mail App</p>
                                        <p className="text-xs text-muted">Uses your default email client</p>
                                    </div>
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-sand bg-surface/50">
                    <p className="text-xs text-muted text-center">
                        Or email directly: <a href={`mailto:${email}`} className="text-terra hover:text-terra-dark">{email}</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ContactModal;
